import { CANVAS_HEIGHT, CANVAS_WIDTH } from "../../game/constants";
import { isMobileViewport } from "../../game/platform";
import { getCharacterById } from "../../economy/characters";
import type { Rect, UiRects } from "../../game/input";
import type { RenderModel } from "../renderModel";
import type { drawButton } from "../ui";
import {
  GAME_FONT,
  MENU_TEXT_PRIMARY,
  menuButtonColumns,
  menuBtnH,
  menuGap,
  mFont,
  mFontPlain,
  roundedRectPath,
  formatDerivedAccountLevel,
} from "../renderConstants";

// ── Dependency interface ────────────────────────────────────────────

export interface FriendsDeps {
  ctx: CanvasRenderingContext2D;
  drawBtn: (rect: Rect, label: string, options?: Parameters<typeof drawButton>[3]) => void;
  drawBtnStagger: (
    rect: Rect,
    label: string,
    index: number,
    options?: Parameters<typeof drawButton>[3],
  ) => void;
  drawMenuPageNavButton: (
    rect: Rect,
    label: string,
    style: { fill: string; stroke: string },
  ) => void;
  drawCharacterPortrait: (imagePath: string, x: number, y: number, w: number, h: number) => void;
  nowMs: () => number;
  measureTextWidth: (text: string) => number;
}

// ── Extracted screen function ───────────────────────────────────────

export function drawFriendsScreen(deps: FriendsDeps, model: RenderModel): UiRects {
  const { ctx, drawBtn, drawBtnStagger, drawMenuPageNavButton, drawCharacterPortrait, nowMs, measureTextWidth } = deps;
  const centerX = CANVAS_WIDTH / 2;
  const menuCols = menuButtonColumns(centerX);
  const uiRects: UiRects = {};
  const mob = isMobileViewport();
  const gap = menuGap();
  const entryH = mob ? 50 : 40;
  const cardH = entryH - 4;
  const cardR = mob ? 6 : 4;
  const nameFontPx = 7;
  const lvlFontPx = 5;
  const sectionFontPx = 7;
  const btnFontPx = 6;

  // Title
  ctx.fillStyle = MENU_TEXT_PRIMARY;
  ctx.font = `bold 11px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText("FRIENDS", centerX, 118);

  const listW = menuCols.fullW;
  const listX = menuCols.fullX;

  let y = 140;

  // ── Pending requests (show first so user sees them) ──
  const pending = model.pendingRequests;
  if (pending.length > 0) {
    ctx.fillStyle = "#ff9966";
    ctx.font = mFont("bold", sectionFontPx);
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText(`REQUESTS (${pending.length})`, centerX, y);
    y += 18 + gap;

    for (let i = 0; i < Math.min(pending.length, 3); i++) {
      const p = pending[i];
      const ey = y;

      roundedRectPath(ctx, listX, ey, listW, cardH, cardR);
      ctx.fillStyle = "rgba(50,30,20,0.5)";
      ctx.fill();
      roundedRectPath(ctx, listX, ey, listW, cardH, cardR);
      ctx.strokeStyle = "rgba(255,153,102,0.3)";
      ctx.lineWidth = 1;
      ctx.stroke();

      // Username
      ctx.fillStyle = "#ffcc99";
      ctx.font = mFont("bold", nameFontPx);
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillText(p.username.toUpperCase().slice(0, 12), listX + 10, ey + cardH * 0.3);

      // Level (below username)
      ctx.fillStyle = "#ffd700";
      ctx.font = mFontPlain(lvlFontPx);
      ctx.fillText(formatDerivedAccountLevel(p.level), listX + 10, ey + cardH * 0.7);

      // ACCEPT button
      const abtnH = mob ? 24 : 20;
      const abtnY = ey + (cardH - abtnH) / 2;
      const acceptBtn: Rect = { x: listX + listW - 100, y: abtnY, w: 46, h: abtnH };
      drawBtnStagger(acceptBtn, "ACCEPT", i * 2, { fill: "#1a3a2a", stroke: "#4dff88", font: mFont("bold", btnFontPx) });
      (uiRects as Record<string, Rect>)[`friendAccept${i}`] = acceptBtn;

      // DECLINE button
      const declineBtn: Rect = { x: listX + listW - 50, y: abtnY, w: 46, h: abtnH };
      drawBtnStagger(declineBtn, "DECLINE", i * 2 + 1, { fill: "#3a1a1a", stroke: "#ff6b6b", font: mFont("bold", btnFontPx) });
      (uiRects as Record<string, Rect>)[`friendDecline${i}`] = declineBtn;

      y += entryH + gap;
    }
    y += gap;
  }

  // ── Sent requests (outgoing, waiting for acceptance) ──
  const sent = model.sentRequests;
  if (sent.length > 0) {
    ctx.fillStyle = "#6a8ab8";
    ctx.font = mFont("bold", sectionFontPx);
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText(`SENT (${sent.length})`, centerX, y);
    y += 18 + gap;

    for (let i = 0; i < Math.min(sent.length, 3); i++) {
      const s = sent[i];
      const ey = y;

      roundedRectPath(ctx, listX, ey, listW, cardH, cardR);
      ctx.fillStyle = "rgba(30,40,60,0.4)";
      ctx.fill();
      roundedRectPath(ctx, listX, ey, listW, cardH, cardR);
      ctx.strokeStyle = "rgba(106,138,184,0.25)";
      ctx.lineWidth = 1;
      ctx.stroke();

      ctx.fillStyle = "#8eb6d9";
      ctx.font = mFont("bold", nameFontPx);
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillText(s.username.toUpperCase().slice(0, 12), listX + 10, ey + cardH / 2);

      ctx.fillStyle = "#6a8ab8";
      ctx.font = mFont("bold", lvlFontPx);
      ctx.textAlign = "right";
      ctx.fillText("PENDING", listX + listW - 10, ey + cardH / 2);

      y += entryH + gap;
    }
    y += gap;
  }

  // ── Friends list ──
  const friends = model.friendsList;
  const portraitSize = mob ? 28 : 24;

  for (let i = 0; i < Math.min(friends.length, 5); i++) {
    const f = friends[i];
    const ey = y;

    // Card-style entry
    roundedRectPath(ctx, listX, ey, listW, cardH, cardR);
    ctx.fillStyle = i % 2 === 0 ? "rgba(30,51,88,0.5)" : "rgba(20,35,60,0.5)";
    ctx.fill();
    roundedRectPath(ctx, listX, ey, listW, cardH, cardR);
    ctx.strokeStyle = f.online ? "rgba(77,255,136,0.25)" : "rgba(61,90,130,0.25)";
    ctx.lineWidth = 1;
    ctx.stroke();

    // Online dot (pulse glow when online)
    if (f.online) {
      const dotPulse = 0.4 + 0.6 * Math.sin(nowMs() / 500 + i);
      ctx.save();
      ctx.shadowColor = "#4dff88";
      ctx.shadowBlur = 4 + 4 * dotPulse;
      ctx.fillStyle = "#4dff88";
      ctx.beginPath();
      ctx.arc(listX + 10, ey + cardH / 2, 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    } else {
      ctx.fillStyle = "#3d5a82";
      ctx.beginPath();
      ctx.arc(listX + 10, ey + cardH / 2, 3, 0, Math.PI * 2);
      ctx.fill();
    }

    // Character portrait
    const charDef = getCharacterById(f.equippedSkin);
    const pX = listX + 20;
    const pY = ey + (cardH - portraitSize) / 2;
    if (charDef) {
      drawCharacterPortrait(charDef.imageSmallPath, pX, pY, portraitSize, portraitSize);
    }

    // Username
    const textX = pX + portraitSize + 6;
    ctx.fillStyle = f.online ? "#d4e7ff" : "#6a8ab8";
    ctx.font = mFont("bold", nameFontPx);
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(f.username.toUpperCase().slice(0, 10), textX, ey + cardH * 0.3);

    // Level (below username)
    ctx.fillStyle = "#ffd700";
    ctx.font = mFontPlain(lvlFontPx);
    ctx.fillText(formatDerivedAccountLevel(f.level), textX, ey + cardH * 0.7);

    // Remove button (always visible)
    const removeBtnH = mob ? 24 : 20;
    const removeBtnY = ey + (cardH - removeBtnH) / 2;
    const removeBtn: Rect = { x: listX + listW - 24, y: removeBtnY, w: 20, h: removeBtnH };
    drawBtn(removeBtn, "X", { fill: "#3a1313", stroke: "#ff4d4d", font: mFont("bold", btnFontPx + 1) });
    (uiRects as Record<string, Rect>)[`friendRemove${i}`] = removeBtn;

    // BATTLE button (only for online friends)
    if (f.online) {
      const bbtnH = mob ? 24 : 20;
      const bbtnY = ey + (cardH - bbtnH) / 2;
      const btn: Rect = { x: listX + listW - 82, y: bbtnY, w: 54, h: bbtnH };
      drawBtn(btn, "BATTLE", { fill: "#3a1a1a", stroke: "#ff6b6b", font: mFont("bold", btnFontPx) });
      (uiRects as Record<string, Rect>)[`friendBattle${i}`] = btn;
    }

    y += entryH + gap;
  }

  // Pagination
  const totalPages = Math.max(1, Math.ceil(model.friendsTotalCount / 5));
  if (totalPages > 1) {
    y += gap;
    const pagY = y;
    const pagBtnH = mob ? 28 : 20;

    // PREV button
    if (model.friendsPage > 0) {
      const prevBtn: Rect = { x: centerX - 80, y: pagY, w: 44, h: pagBtnH };
      drawMenuPageNavButton(prevBtn, "<", { fill: "#1a2a4c", stroke: "#5c8cd4" });
      uiRects.friendsPagePrev = prevBtn;
    }

    // Page indicator
    ctx.fillStyle = "#6a8ab8";
    ctx.font = mFont("bold", 6);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(`${model.friendsPage + 1} / ${totalPages}`, centerX, pagY + pagBtnH / 2);

    // NEXT button
    if (model.friendsPage < totalPages - 1) {
      const nextBtn: Rect = { x: centerX + 40, y: pagY, w: 44, h: pagBtnH };
      drawMenuPageNavButton(nextBtn, ">", { fill: "#1a2a4c", stroke: "#5c8cd4" });
      uiRects.friendsPageNext = nextBtn;
    }

    y += pagBtnH + gap;
  }

  // Bottom buttons (matching menu button layout)
  const bottomY = Math.max(y + gap, 470);
  const friendsBack: Rect = { x: menuCols.leftX, y: bottomY, w: menuCols.halfW, h: menuBtnH() };
  drawBtn(friendsBack, "BACK", { fill: "#1a2a4c", stroke: "#5c8cd4", font: mFont("bold", 7) });
  uiRects.friendsBack = friendsBack;

  const addFriendBtn: Rect = { x: menuCols.rightX, y: bottomY, w: menuCols.halfW, h: menuBtnH() };
  drawBtn(addFriendBtn, "ADD FRIEND", { fill: "#1a3a2a", stroke: "#4dff88", font: mFont("bold", 7) });
  uiRects.openAddFriend = addFriendBtn;

  const removalConfirm = model.friendRemoveConfirm;
  if (removalConfirm != null) {
    ctx.fillStyle = "rgba(0,0,0,0.72)";
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    const dialogW = menuCols.fullW;
    const dialogH = 138;
    const dialogX = centerX - dialogW / 2;
    const dialogY = CANVAS_HEIGHT / 2 - dialogH / 2;

    roundedRectPath(ctx, dialogX, dialogY, dialogW, dialogH, 8);
    ctx.fillStyle = "rgba(15,20,40,0.98)";
    ctx.fill();
    roundedRectPath(ctx, dialogX, dialogY, dialogW, dialogH, 8);
    ctx.strokeStyle = "#ff6b6b";
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.fillStyle = "#ffd5d5";
    ctx.font = mFont("bold", 7);
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText("REMOVE FRIEND?", centerX, dialogY + 10);

    ctx.fillStyle = "#ff9c9c";
    ctx.font = mFont("bold", 5);
    ctx.fillText(removalConfirm.username.toUpperCase().slice(0, 14), centerX, dialogY + 28);

    const promptMaxW = dialogW - 24;
    const promptWords = removalConfirm.prompt.split(" ");
    const promptLines: string[] = [];
    let line = "";
    ctx.fillStyle = "#f2e3e3";
    ctx.font = mFont("bold", 5);
    for (const word of promptWords) {
      const next = line.length === 0 ? word : `${line} ${word}`;
      if (measureTextWidth(next) <= promptMaxW || line.length === 0) {
        line = next;
      } else {
        promptLines.push(line);
        line = word;
      }
    }
    if (line.length > 0) {
      promptLines.push(line);
    }
    let promptY = dialogY + 46;
    for (const promptLine of promptLines.slice(0, 3)) {
      ctx.fillText(promptLine, centerX, promptY);
      promptY += 12;
    }

    // Full-screen dismiss layer behind modal buttons.
    uiRects.friendRemoveDismiss = { x: 0, y: 120, w: CANVAS_WIDTH, h: CANVAS_HEIGHT - 120 };
    if (model.friendRemoveBusy) {
      ctx.fillStyle = "#ffb3b3";
      ctx.font = mFont("bold", 6);
      ctx.fillText("REMOVING...", centerX, dialogY + dialogH - 22);
    } else {
      const modalBtnW = (dialogW - 24) / 2;
      const modalBtnH = 28;
      const modalBtnY = dialogY + dialogH - modalBtnH - 10;
      const cancelBtn: Rect = { x: dialogX + 8, y: modalBtnY, w: modalBtnW, h: modalBtnH };
      const confirmBtn: Rect = { x: dialogX + 8 + modalBtnW + 8, y: modalBtnY, w: modalBtnW, h: modalBtnH };
      drawBtn(cancelBtn, "CANCEL", { fill: "#2a1a1a", stroke: "#ff6b6b", font: mFont("bold", 6) });
      drawBtn(confirmBtn, "REMOVE", { fill: "#3a1313", stroke: "#ff4d4d", font: mFont("bold", 6) });
      uiRects.friendRemoveCancel = cancelBtn;
      uiRects.friendRemoveConfirm = confirmBtn;
    }
  }

  return uiRects;
}
